import "./StripePaymentUpdatePage.scss";

import { Alert, Flex, Header, Loader, Text } from "@fluentui/react-northstar";
import { UserDTO, UserSubscriptionDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

import { useDatadog } from "../../_shared/actions/useDatadog";
import CoreConfirmationAnimation from "../../_shared/assets/confirmation-image-ok-mark.svg";
import StripePoweredBy from "../../_shared/assets/powered-by-stripe.svg";
import { StripeUpdatePayment } from "../../_shared/components/Checkout/StripeUpdatePayment";
import { ContactUsLink } from "../../_shared/components/ContactUsLink";
import { isSuccess } from "../../_shared/services/ChannelApi";
import { userApi } from "../../_shared/services/UserApi";
import { getPlanLabel } from "../../_shared/types/subscriptionHelper";
import { clientEventTracker } from "../../_shared/utils/clientTelemetry";
import { getPlanLevelDisplayText } from "../../_shared/utils/paymentHelpers";
import { MSALPageHost } from "../MSALPageHost";
import { PageHeader } from "../PageHeader";
import { useMicrosoftAuth } from "../useMicrosoftAuth";

export const StripePaymentUpdateComponent: React.FC = () => {
  const { result } = useMicrosoftAuth(["openid", "profile", "email"]);
  const [user, setUser] = useState<UserDTO>();
  const [paramErrors, setParamErrors] = useState<string[]>([]);
  const [stripeSubscription, setStripeSubscription] = useState<UserSubscriptionDTO>();
  const [updateComplete, setUpdateComplete] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>();

  const { initialize: dataDogInit, log } = useDatadog("teams-stripe-payment-update", 100, 0);

  useEffect(() => {
    if (!result?.idToken) {
      return;
    }

    (async () => {
      const u = await userApi.getUserWithToken(result.idToken);
      if (isSuccess(u)) {
        setUser(u);
        dataDogInit(u.userId);
      } else {
        log({ logLevel: "error", message: "Failed to get user: " + u.message });
        setAuthError(u.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.idToken]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const stripeSubscription = [...user.subscriptions, ...user.adminSubscriptions].find(
      sub => sub.provider === "stripe" && sub.state === "active" && sub.type === "full"
    );

    if (!stripeSubscription) {
      setParamErrors(["Unable to find an active stripe subscription"]);
      return;
    }

    clientEventTracker.track("update payment page viewed", user, {
      subscriptionId: stripeSubscription.providerSubId,
    });

    setStripeSubscription(stripeSubscription);
  }, [user]);

  const onStripeDialogCompleted = () => {
    setUpdateComplete(true);
  };

  if (authError) {
    return (
      <Alert content="Oops, something went wrong please try again or contact support" warning />
    );
  }

  if (!user || !result?.idToken) {
    return <Loader />;
  }

  return (
    <div className="stripe-payment-update">
      <PageHeader
        helpLink={{
          url: "https://www.polly.ai/help/microsoft-teams/buying-polly",
          label: "What is this?",
        }}
      />

      <div className="stripe-payment-update__container">
        {paramErrors.length === 0 && stripeSubscription && !updateComplete && (
          <div>
            <Header as="h2" align="center" className="stripe-payment-update__title">
              Update payment information
            </Header>
            <Flex gap="gap.large">
              <Flex.Item grow>
                <div>
                  <Text weight="bold" size="large">
                    Payment Information
                  </Text>
                  <StripeUpdatePayment
                    user={user}
                    authToken={result.idToken}
                    onCompleted={onStripeDialogCompleted}
                    subscriptionId={stripeSubscription.id}
                  />
                </div>
              </Flex.Item>
              <Flex.Item grow>
                <div>
                  <Text weight="bold" size="large">
                    Plan Information
                  </Text>
                  <ul className="stripe-payment-update__payment-for">
                    <li>
                      <Text>User: {user.name}</Text>
                    </li>
                    {user.upn && (
                      <li>
                        <Text>User UPN: {user.upn}</Text>
                      </li>
                    )}
                    <li>
                      <Text>
                        Current Plan:{" "}
                        {getPlanLabel(
                          stripeSubscription.plan,
                          stripeSubscription.planLevel,
                          stripeSubscription.scope
                        )}
                      </Text>
                    </li>
                    {stripeSubscription.planQuantity && (
                      <li>
                        <Text>Seats: {stripeSubscription.planQuantity}</Text>
                      </li>
                    )}
                    <li>
                      <Text>
                        Monthly Responses:{" "}
                        {getPlanLevelDisplayText(user.featureFlags.responseUsageLimit)}
                      </Text>
                    </li>
                  </ul>
                </div>
              </Flex.Item>
            </Flex>

            <Flex className="stripe-payment-update__buttons" space="between">
              <Flex.Item>
                <a href="https://www.stripe.com" target="_blank" rel="noopener noreferrer">
                  <img src={StripePoweredBy} alt="Powered by Stripe" height={20} />
                </a>
              </Flex.Item>

              <Flex.Item align="center">
                <ContactUsLink user={user} label="Contact Sales" />
              </Flex.Item>
            </Flex>
          </div>
        )}
        {paramErrors.length === 0 && updateComplete && (
          <>
            <Text
              content="Congratulations! Your Polly subscription has been updated."
              size="larger"
              weight="bold"
              align="center"
              as="div"
            />

            <img
              src={CoreConfirmationAnimation}
              className="congratulations__success_img"
              alt="Success"
              width="100%"
              height={150}
            />

            <div className="stripe-payment-update__payment-success-details">
              <Text>
                Close this window and return to the billing page in the Teams application to see the
                updated details
              </Text>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const StripePaymentUpdatePage: React.FC = () => {
  return (
    <MSALPageHost
      title={
        <>
          Sign in to verify and <Text weight="bold"> upgrade your Polly account</Text>
        </>
      }
    >
      <StripePaymentUpdateComponent />
    </MSALPageHost>
  );
};
